<template>
    <div>
        <b-row>
            <b-col cols="6">
                <fg-input label="Start Date" required>
                    <el-date-picker v-model="startDate" format="MM/dd/yyyy" type="date"></el-date-picker>
                </fg-input>
            </b-col>
            <b-col cols="6">
                <fg-input label="End Date" required>
                    <el-date-picker v-model="endDate" format="MM/dd/yyyy" type="date"></el-date-picker>
                </fg-input>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-form-group label="Send To">
                    <b-form-select v-model="sendTo" :options="sendToOptions"></b-form-select>
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-button :variant="pdfSource ? 'info' : 'outline-info'" @click="viewReport" :disabled="isLoadingReport">
                    <i v-if="isLoadingReport" class="fa fa-spinner fa-spin fa-fw mr-1"></i>
                    <i v-else :class="{ 'fa fa-chevron-down fa-fw mr-1': true, 'fa-rotate-180': !pdfSource }" style="transition: all .2s;"></i>
                    {{ isLoadingReport ? 'Loading...' : 'View Report' }}
                </b-button>
                <b-button variant="primary" @click="sendReport" :disabled="!sendTo || isSending" class="float-right">
                    Send Report
                </b-button>
            </b-col>
        </b-row>
        <b-row v-if="pdfSource">
            <b-col>
                <b-embed type="embed" :src="pdfSource" style="width: 100%; height: 100%;" />
            </b-col>
        </b-row>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import axios from 'axios';
import { Patient } from '../../models/Patient';
import { IRSStatement } from '../../models/IRSStatement';
import { DatePicker } from 'element-ui'
import moment from 'moment'
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

@Component({
    name: "IRSReportComponent",
    components: {
        [DatePicker.name]: DatePicker
    }
})
export default class IRSReportComponent extends Vue {
    @Prop() patient!: Patient;
    private startDate: Date = new Date(`01/01/${IRSReportComponent.getDefaultYear()}`);
    private endDate: Date = new Date(`12/31/${IRSReportComponent.getDefaultYear()}`);
    private sendTo: string = "";

    private static getDefaultYear(): number {
        const now = new Date();
        const aprilFifteen = new Date(now.getFullYear(), 3, 15); // Month is 0-indexed
        return now < aprilFifteen ? now.getFullYear() - 1 : now.getFullYear();
    }

    private pdfSource: string | null = null;
    private isSending = false;
    private isLoadingReport = false;
    private canSendEmail = false;


    created() {
        console.log(this.patient);
        this.getIRSReportEmailFromConfig();
    }

    get sendToOptions() {
        return [
            {
                value: this.patient.emailAddress,
                text: this.patient.emailAddress,
                visible: this.canSendEmail && !!this.patient.emailAddress?.trim()
            },
            {
                value: this.patient.phoneNumber,
                text: this.patient.phoneNumber,
                visible: !!this.patient.phoneNumber?.trim()
            },
            {
                value: this.patient.cellNumber,
                text: this.patient.cellNumber,
                visible: !!this.patient.cellNumber?.trim()
            }
        ].filter(i => i.visible !== false);
    }

    async viewReport() {
        if (this.isLoadingReport) return;

        if (this.pdfSource) {
            // Toggle off - clear the PDF source
            this.pdfSource = null;
            return;
        }

        try {
            this.isLoadingReport = true;
            const url = `reports/embeddedIRS/${this.patient.id}/${moment(this.startDate).format("YYYY-MM-DD")}/${moment(this.endDate).format("YYYY-MM-DD")}`;
            this.pdfSource = URL.createObjectURL((await axios.get(url, { responseType: "blob" })).data);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isLoadingReport = false;
        }
    }

    async sendReport() {
        if (this.isSending) return;

        let data: IRSStatement = new IRSStatement;

        data.patientId = this.patient.id as number;
        data.startDate = this.startDate;
        data.endDate = this.endDate;
        data.deliveryMethod = "SendToPatient";
        data.sendTo = this.sendTo;

        try {
            this.isSending = true;
            await axios.post(`/reports/IRSStatement/Send`, data);

            this.$notification(NotificationOptions.success("IRS Statement Sent Successfully"));
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        }

        this.isSending = false;
    }

    async getIRSReportEmailFromConfig() {
        try {
            const response = await this.$http.get<boolean>("/Configuration/IRSReportEmail");
            this.canSendEmail = response.data;
        } catch {
            // ignore
        }

        this.sendTo = this.sendToOptions[0].value;
    }
}
</script>
